"use client";

import { c as _c } from "react/compiler-runtime";
import { renderProviderMentions } from "../lib/render-mentions";
import { useState } from "react";
import { RiDeleteBinLine, RiEditLine, RiMore2Fill } from "react-icons/ri";
import { MessageAttachments } from "@/components/team-chat/message-attachments";
import { TaskMentionEditor } from "@/components/tiptap/task-mention-editor";
import Button from "@/components/ui/button";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from "@/components/ui/dropdown";
import { useUser } from "@/contexts/user-provider";
import { useCanWriteTasks } from "@/hooks/use-can-write-tasks";
import { useProviders } from "@/hooks/use-providers";
import { calculateRelativeDuration, formatStaffName } from "@/lib/format-data";
import { TaskCommentDetail, TaskLogDetail } from "@/types/task";
import { formatActivityDescription, getActivityIcon } from "./task-activity-log";
import { useTaskActivity, useTaskMutations } from "@/api/provider/tasks/tasks-query";
interface TaskActivityTimelineProps {
  taskId: string;
  comments: TaskCommentDetail[];
}
type TimelineItem = {
  type: "comment";
  data: TaskCommentDetail;
} | {
  type: "activity";
  data: TaskLogDetail;
};
export function TaskActivityTimeline(t0) {
  const $ = _c(21);
  const {
    taskId,
    comments
  } = t0;
  const {
    data: activities,
    isLoading: isLoadingActivity
  } = useTaskActivity(taskId);
  const {
    providers
  } = useProviders();
  const {
    currentUser
  } = useUser();
  const {
    updateComment,
    deleteComment
  } = useTaskMutations();
  const canWriteTasks = useCanWriteTasks();
  const [editingCommentId, setEditingCommentId] = useState(null);
  const [editContent, setEditContent] = useState("");
  let t1;
  if ($[0] !== activities || $[1] !== canWriteTasks || $[2] !== comments || $[3] !== currentUser?.id || $[4] !== deleteComment || $[5] !== editContent || $[6] !== editingCommentId || $[7] !== isLoadingActivity || $[8] !== providers || $[9] !== taskId || $[10] !== updateComment) {
    const timeline = [...comments.map(_temp), ...(activities || []).filter(_temp2).map(_temp3)].sort(_temp4);
    let t2;
    if ($[12] === Symbol.for("react.memo_cache_sentinel")) {
      t2 = comment => {
        setEditingCommentId(comment.id);
        setEditContent(comment.content);
      };
      $[12] = t2;
    } else {
      t2 = $[12];
    }
    const handleStartEdit = t2;
    let t3;
    if ($[13] === Symbol.for("react.memo_cache_sentinel")) {
      t3 = () => {
        setEditingCommentId(null);
        setEditContent("");
      };
      $[13] = t3;
    } else {
      t3 = $[13];
    }
    const handleCancelEdit = t3;
    let t4;
    if ($[14] !== editContent || $[15] !== taskId || $[16] !== updateComment) {
      t4 = async commentId => {
        if (!editContent.trim()) {
          return;
        }
        try {
          await updateComment.mutateAsync({
            taskId,
            commentId,
            comment: {
              content: editContent.trim()
            }
          });
          setEditingCommentId(null);
          setEditContent("");
        } catch {}
      };
      $[14] = editContent;
      $[15] = taskId;
      $[16] = updateComment;
      $[17] = t4;
    } else {
      t4 = $[17];
    }
    const handleSaveEdit = t4;
    let t5;
    if ($[18] !== deleteComment || $[19] !== taskId) {
      t5 = commentId_0 => {
        deleteComment.mutate({
          taskId,
          commentId: commentId_0
        });
      };
      $[18] = deleteComment;
      $[19] = taskId;
      $[20] = t5;
    } else {
      t5 = $[20];
    }
    const handleDelete = t5;
    t1 = <div className="space-y-6">{isLoadingActivity ? <div className="flex items-center justify-center py-8"><div className="h-5 w-5 animate-spin rounded-full border-2 border-foreground-muted border-t-transparent" /></div> : timeline.length === 0 ? <p className="text-body text-foreground-secondary">No activity yet</p> : <div className="space-y-6">{timeline.map(item => {
          if (item.type === "comment") {
            const comment_0 = item.data;
            const isOwner = comment_0.authorId === currentUser?.id;
            const isEditing = editingCommentId === comment_0.id;
            return <div key={`comment-${comment_0.id}`} className="flex gap-3"><div className="flex-shrink-0 mt-0.5"><div className="size-8 rounded-full bg-background-secondary flex items-center justify-center text-caption-strong text-foreground">{comment_0.author ? `${comment_0.author.firstName?.[0] ?? ""}${comment_0.author.lastName?.[0] ?? ""}` : "?"}</div></div><div className="flex-1 min-w-0"><div className="flex items-center gap-2 mb-1"><span className="text-body-strong text-foreground">{comment_0.author ? formatStaffName(comment_0.author) : "Unknown"}</span><span className="text-caption text-foreground-muted">{calculateRelativeDuration(comment_0.createdDate)}</span>{comment_0.updatedDate && <span className="text-caption text-foreground-muted italic">(edited)</span>}{isOwner && canWriteTasks && !isEditing && <DropdownMenu><DropdownMenuTrigger asChild={true}><button className="ml-auto p-1 rounded hover:bg-hover text-foreground-muted hover:text-foreground transition-colors"><RiMore2Fill className="size-3.5" /></button></DropdownMenuTrigger><DropdownMenuContent align="end">{!!comment_0.content && <DropdownMenuItem onClick={() => handleStartEdit(comment_0)}><RiEditLine className="size-4 mr-2" />Edit</DropdownMenuItem>}<DropdownMenuItem onClick={() => handleDelete(comment_0.id)}><RiDeleteBinLine className="size-4 mr-2" />Delete</DropdownMenuItem></DropdownMenuContent></DropdownMenu>}</div>{isEditing ? <div className="space-y-2"><TaskMentionEditor value={editContent} onChange={setEditContent} onEnter={() => handleSaveEdit(comment_0.id)} /><div className="flex gap-2 justify-end"><Button variant="secondary" size="sm" onClick={handleCancelEdit}>Cancel</Button><Button variant="primary" size="sm" onClick={() => handleSaveEdit(comment_0.id)} disabled={!editContent.trim() || updateComment.isPending}>Save</Button></div></div> : <div className="rounded-lg border border-border bg-background-secondary p-3 space-y-2">{!!comment_0.content && <p className="text-body text-foreground-secondary whitespace-pre-wrap">{renderProviderMentions(comment_0.content, providers)}</p>}{!!comment_0.attachments?.length && <MessageAttachments files={comment_0.attachments} />}</div>}</div></div>;
          } else {
            const log = item.data;
            const {
              icon: Icon,
              color
            } = getActivityIcon(log);
            return <div key={`activity-${log.id}`} className="flex items-center gap-3 pl-2.5"><div className="flex-shrink-0 w-8 flex justify-center"><Icon className={`size-3.5 ${color}`} /></div><div className="flex-1 flex items-center gap-2 min-w-0"><span className="text-caption text-foreground-secondary truncate">{formatActivityDescription(log)}</span><span className="text-caption text-foreground-muted shrink-0">{calculateRelativeDuration(log.createdDate)}</span></div></div>;
          }
        })}</div>}</div>;
    $[0] = activities;
    $[1] = canWriteTasks;
    $[2] = comments;
    $[3] = currentUser?.id;
    $[4] = deleteComment;
    $[5] = editContent;
    $[6] = editingCommentId;
    $[7] = isLoadingActivity;
    $[8] = providers;
    $[9] = taskId;
    $[10] = updateComment;
    $[11] = t1;
  } else {
    t1 = $[11];
  }
  return t1;
}
function _temp4(a_1, b) {
  return new Date(a_1.data.createdDate).getTime() - new Date(b.data.createdDate).getTime();
}
function _temp3(a_0) {
  return {
    type: "activity" as const,
    data: a_0
  };
}
function _temp2(a) {
  return a.actionType !== "COMMENT_ADDED";
}
function _temp(c) {
  return {
    type: "comment" as const,
    data: c
  };
}